import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { subscribe as subscribeAdminWs, send as sendAdminWs } from '../utils/wsAdmin';

/**
 * Live screen-mirror viewer -- ported from primus-admin-main's
 * ScreenMirrorModal.jsx. Only difference: this app's admin has no single
 * café (a superadmin token), so every mirror.subscribe/unsubscribe frame
 * must include an explicit cafe_id -- app/ws/admin.py's superadmin path
 * requires it (a non-superadmin's cafe_id is taken from their own token
 * instead and this field is ignored for them).
 */
const RENEW_MS = 3000;      // lease renewal cadence (backend lease is ~6s)
const STALL_MS = 4000;      // no frame for this long => show "reconnecting"

export default function ScreenMirrorModal({ pc, cafeId, onClose }) {
    const [frame, setFrame] = useState(null);
    const [stalled, setStalled] = useState(false);
    const [everReceived, setEverReceived] = useState(false);
    const lastFrameAt = useRef(0);

    useEffect(() => {
        if (!pc?.id || !cafeId) return;
        let alive = true;

        const unsub = subscribeAdminWs((msg) => {
            if (!alive || !msg || msg.event !== 'mirror.frame') return;
            const p = msg.payload || {};
            if (p.pc_id !== pc.id || !p.data) return;
            lastFrameAt.current = Date.now();
            setEverReceived(true);
            setStalled(false);
            setFrame(`data:image/jpeg;base64,${p.data}`);
        });

        const renew = () => sendAdminWs({ event: 'mirror.subscribe', payload: { pc_id: pc.id, cafe_id: cafeId } });
        renew();
        const renewId = setInterval(renew, RENEW_MS);

        const stallId = setInterval(() => {
            if (lastFrameAt.current && Date.now() - lastFrameAt.current > STALL_MS) {
                setStalled(true);
            }
        }, 1000);

        return () => {
            alive = false;
            clearInterval(renewId);
            clearInterval(stallId);
            try { sendAdminWs({ event: 'mirror.unsubscribe', payload: { pc_id: pc.id, cafe_id: cafeId } }); } catch { /* ignore */ }
            try { unsub && unsub(); } catch { /* ignore */ }
        };
    }, [pc?.id, cafeId]);

    if (!pc) return null;

    return (
        <div
            style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
            onClick={onClose}
        >
            <div
                style={{ background: '#0b0f17', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 16, width: '100%', maxWidth: 1100, color: '#fff', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ width: 9, height: 9, borderRadius: '50%', background: stalled ? '#f59e0b' : '#ef4444', boxShadow: stalled ? 'none' : '0 0 8px #ef4444' }} />
                        <span style={{ fontWeight: 700, fontSize: 16 }}>Mirroring · {pc.name}</span>
                        <span style={{ fontSize: 12, color: '#9CA3AF' }}>~1 fps · live</span>
                    </div>
                    <button
                        type="button" onClick={onClose} title="Stop mirroring"
                        style={{ background: 'transparent', border: 'none', color: '#9CA3AF', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: 4 }}
                    >
                        <X size={22} />
                    </button>
                </div>

                <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {frame ? (
                        <img src={frame} alt={`Screen of ${pc.name}`} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} />
                    ) : (
                        <div style={{ color: '#9CA3AF', fontSize: 14 }}>
                            {!pc.online ? 'PC is offline — no screen to mirror.' : 'Waiting for the first frame…'}
                        </div>
                    )}

                    {stalled && everReceived && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', color: '#fcd34d', fontSize: 14 }}>
                            Frames paused — reconnecting…
                        </div>
                    )}
                </div>

                <div style={{ padding: '10px 18px', fontSize: 12, color: '#9CA3AF', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    The customer is notified their screen may be viewed by staff. Frames are live-only and never recorded.
                </div>
            </div>
        </div>
    );
}
